<template>
  <v-dialog
    v-if="dialog"
    :value="dialog"
    :max-width="1000"
    persistent
    :retain-focus="false"
    scrollable
    class="p-send-dialog"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
  >
    <v-card outlined>
      <v-card-title>
        {{ $t("email.newMessage") }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-form ref="form" @submit.stop.prevent>
          <v-container>
            <v-row class="pt-5">
              <v-col>
                <v-combobox
                  ref="to"
                  v-model="newEmail.to"
                  :items="contactEmailList"
                  :label="$t('email.edit.to')"
                  :rules="rules.to"
                  item-value="contactEmail"
                  item-text="displayNameWithCompany"
                  prepend-icon="mdi-account-multiple-outline"
                  tabindex="1"
                  clearable
                  validate-on-blur
                  multiple
                  small-chips
                  deletable-chips
                  return-object
                  autofocus
                  @change="onEmailToChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title
                        v-text="item.displayNameWithCompany"
                        :class="{'is-member': item.isMember }"
                      ></v-list-item-title>
                      <v-list-item-subtitle
                        v-text="item.email"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-combobox>
                <v-text-field
                  v-model.trim="newEmail.subject"
                  :label="$t('email.edit.subject')"
                  :rules="rules.subject"
                  type="text"
                  prepend-icon="mdi-text-short"
                  tabindex="1"
                  clearable
                  validate-on-blur
                  @input="onSubjectInput"
                >
                </v-text-field>
<!--                <p-wysiwyg-editor-->
<!--                  v-model="newEmail.body"-->
<!--                  class="mt-4 ml-9"-->
<!--                  tabindex="1"-->
<!--                  clearable-->
<!--                  @input="onBodyInput"-->
<!--                ></p-wysiwyg-editor>-->
                <div class="mt-4 ml-9">
                  <PSyncfusionRichTextEditor
                    v-model="newEmail.body"
                    :label="$t('socialMediaPost.edit.message')"
                    tabindex="1"
                    @input="onBodyInput"/>
                </div>

                <div v-if="presetAttachments && presetAttachments.length" class="p-attachments d-flex">
                  <div>
                    <v-icon class="mdi-rotate-270">mdi-attachment</v-icon>
                  </div>
                  <div class="ml-3">
                    <div v-for="attachment in presetAttachments" :key="`attachment-${attachment.id}`">
                      <p-file-icon :extension="attachment.extension"></p-file-icon>
                      <span class="ml-2">{{attachment.name}}</span>
                    </div>
                  </div>
                </div>

                <div class="d-flex">
                  <v-icon class="mdi-rotate-270 mr-3 d-flex flex-column justify-start pt-4">mdi-attachment</v-icon>
                  <p-file-input
                    v-model="files"
                    :library-file-type="fileType.emailAttachment"
                    :label="$t('email.edit.attachments')"
                    hide-details="auto"
                    multiple
                    clearable
                    @change="onFilesChanged"
                  ></p-file-input>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t("cancel") }}</v-btn>
        <v-btn
          text
          :disabled="!isModelChanged()"
          color="primary"
          tabindex="2"
          @click="submit"
        >
          {{ $t("email.edit.send") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'

import { fileType } from '@/constants'

// import PWysiwygEditor from '@/components/PWysiwygEditor'
import PSyncfusionRichTextEditor from '@/components/PSyncfusionRichTextEditor'

import PBtnIcon from '@/components/PBtnIcon'
import PFileInput from '@/components/PFileInput'
import PFileIcon from '@/components/PFileIcon'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    // PWysiwygEditor,
    PSyncfusionRichTextEditor,
    PBtnIcon,
    PFileInput,
    PFileIcon
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    email: null,
    thread: null,
    presetAttachments: null
  },


  data () {
    return {
      fileType,
      rule: {
        validEmails: value => {
          let arrayValid = true
          for (let i = 0; i < value.length; i++) {
            const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            if (!pattern.test(value[i]) && !pattern.test(value[i].contactEmail)) {
              arrayValid = false
              break
            }
          }
          return arrayValid || this.$t('validation.invalidEmail')
        }
      },
      fullEmail: {
        email: {
          to: [],
          subject: '',
          attachments: []
        },
        files: []
      },
      newEmail: {},
      files: [],
      to: []
    }
  },


  computed: {
    ...mapGetters('contact', ['shortContactsForProject']),
    ...mapGetters('project', ['currentProjectId']),

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    contactEmailList () {
      const list = this.contactsList.filter(c => c.email != null).map(contact => {
        return {
          ...contact,
          contactId: contact.id,
          contactEmail: contact.email
        }
      })
      return list
    },

    rulesToApply () {
      const rule = this.rule

      return {
        to: [
          rule.required(),
          rule.validEmails
        ],
        subject: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('thread', ['sendEmail', 'createThread', 'createAttachments']),

    onDialogOpen () {
      this.initData()
      this.updateFullEmail()
      this.initWatchedModel('fullEmail')
      this.resetValidation()
    },

    onEmailToChange () {
      this.$refs.to.lazySearch = ''
      this.updateFullEmail()
    },

    onSubjectInput () {
      this.updateFullEmail()
    },

    onBodyInput () {
      this.updateFullEmail()
    },

    onFilesChanged () {
      this.updateFullEmail()
    },

    updateFullEmail () {
      this.fullEmail = {
        email: {
          to: [],
          subject: '',
          attachments: []
        },
        files: []
      }

      if (this.newEmail?.to?.length) {
        this.fullEmail.email.to = this.newEmail.to.map(contact => {
          return {
            contactId: contact.contactId ? contact.contactId : null,
            contactEmail: contact.contactEmail ? contact.contactEmail : contact
          }
        })
      }
      if (this.newEmail?.subject) {
        this.fullEmail.email.subject = this.newEmail.subject
      }
      if (this.newEmail?.body) {
        this.fullEmail.email.body = this.newEmail.body
      }
      if (this.files?.length) {
        this.fullEmail.files = this.files
      }
    },

    initData () {
      this.newEmail = {}
      this.files = []
      if (this.thread) {
        this.newEmail = this.getObjectCopy(this.email)
      }
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      this.showSnackbarProcessing()

      if (this.files?.length) {
        const createdAttachments = await this.createAttachments(this.files)
        this.fullEmail.email.attachments = [...createdAttachments.map(el => el.id)]
      }

      if (this.presetAttachments?.length) {
        this.fullEmail.email.attachments.push(...this.presetAttachments.map(el => el.id))
      }

      delete this.fullEmail.files

      try {
        if (this.thread == null) {
          await this.createThread(this.fullEmail)
        } else {
          this.fullEmail.email.threadId = this.thread
          await this.sendEmail(this.fullEmail)
        }
        this.hideSnackbar()
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>

<style>
  .is-member {
    font-weight: bold;
  }
</style>
